import { useQuery } from '@tanstack/react-query';
import { getDashboardSummary, DashboardSummary } from '@/services/dashboardService';
import { useAuth } from '@/contexts/AuthContext';
import { config } from '@/config/config';
import { logError } from '@/utils/errorHandler';

/**
 * Custom hook for fetching unified dashboard data
 * This replaces the previous multi-request approach with a single API call
 */
export function useDashboardData() {
  const { user } = useAuth();
  const organizationId = user?.organization_id;

  const { data, isLoading, error, refetch } = useQuery<DashboardSummary, Error>({
    queryKey: ['dashboard', 'unified', organizationId],
    queryFn: () => getDashboardSummary(organizationId),
    staleTime: config.dashboard.cacheTTL, // Use cache TTL from config
    retry: 2,
    enabled: !!organizationId, // Only run query if we have an organization ID
  });

  // Provide fallback data structure when no data is available
  const fallbackData: DashboardSummary = {
    total_products: 0,
    inactive_product_count: 0,
    data_completeness: 0,
    most_missing_fields: [],
    active_products: 0,
    inactive_products: 0,
    attributes_missing_count: 0,
    mandatory_attributes: [],
    recent_products: [],
    recent_activity: [],
    incomplete_products: [],
    completeness_trend: {
      dates: [],
      completeness: [],
      delta: 0,
      current: 0,
      trend_direction: 'stable',
    },
    family_completeness: [],
  };

  return {
    data: {
      summary: data || fallbackData,
      activity: data?.recent_activity || [],
      incompleteProducts: data?.incomplete_products || [],
    },
    loading: {
      summary: isLoading,
      activity: isLoading,
      incompleteProducts: isLoading,
    },
    error: {
      summary: error,
      activity: error,
      incompleteProducts: error,
    },
    // Provide methods for backward compatibility
    invalidateCache: () => refetch(),
    fetchAll: async (force = false) => {
      if (force) {
        try {
          const result = await refetch();
          return result;
        } catch (error) {
          logError(
            'Failed to refresh dashboard data',
            {
              component: 'useDashboardData',
              action: 'fetchAll',
              organizationId,
              metadata: { force },
            },
            {
              showToast: true,
              userFacing: true,
            }
          );
          throw error;
        }
      }
      return Promise.resolve();
    },
    fetchSummary: async (force = false) => {
      if (force) {
        try {
          const result = await refetch();
          return result;
        } catch (error) {
          logError(
            'Failed to load dashboard summary',
            {
              component: 'useDashboardData',
              action: 'fetchSummary',
              organizationId,
              metadata: { force },
            },
            {
              showToast: true,
              userFacing: true,
            }
          );
          throw error;
        }
      }
      return Promise.resolve();
    },
    fetchActivity: async (force = false) => {
      if (force) {
        try {
          const result = await refetch();
          return result;
        } catch (error) {
          logError(
            'Failed to load recent activity',
            {
              component: 'useDashboardData',
              action: 'fetchActivity',
              organizationId,
              metadata: { force },
            },
            {
              showToast: true,
              userFacing: true,
            }
          );
          throw error;
        }
      }
      return Promise.resolve();
    },
    fetchIncompleteProducts: async (force = false) => {
      if (force) {
        try {
          const result = await refetch();
          return result;
        } catch (error) {
          logError(
            'Failed to load incomplete products',
            {
              component: 'useDashboardData',
              action: 'fetchIncompleteProducts',
              organizationId,
              metadata: { force },
            },
            {
              showToast: true,
              userFacing: true,
            }
          );
          throw error;
        }
      }
      return Promise.resolve();
    },
  };
}
